import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";

import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const formatMoney = (value) => {
  const n = value == null ? 0 : Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

export default function InvoicesMobile() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => setPage(1), [search]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["m-invoices", search, page, pageSize],
    queryFn: () =>
      get("/api/sales-orders", {
        sort: "updatedAt:desc",
        pagination: { page, pageSize },
        populate: { customer: true },
        filters: {
          documentType: { $eq: "invoice" },
          ...(search && {
            $or: [{ salesOrderNumber: { $containsi: search } }, { status: { $containsi: search } }],
          }),
        },
      }),
    staleTime: 10_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  return (
    <IosPage>
      <MobileTopBar
        title="Invoices"
        subtitle={typeof total === "number" ? `${total} total • page ${page}/${pageCount}` : isFetching ? "Syncing…" : "List"}
        onBack={() => navigate("/m/dashboard")}
      />

      <Box sx={{ mt: 1, mb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search invoices…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Failed to load invoices.
        </Typography>
      ) : null}

      <IosList>
        {rows.map((inv) => {
          const documentId = inv.documentId;
          const custRaw = inv?.customer?.data || inv?.customer || null;
          const customer = custRaw ? normalize(custRaw) : null;
          const customerName = customer?.name || "";
          const totalMoney = inv.totalWithTax != null ? formatMoney(inv.totalWithTax) : formatMoney(inv.total);
          return (
            <IosListItem
              key={documentId || inv.id}
              icon="request_quote"
              title={inv.salesOrderNumber || "Invoice"}
              meta={customerName || inv.status || "open"}
              trailing={totalMoney}
              onClick={() => {
                if (documentId) navigate(`/m/invoices/${documentId}`);
              }}
            />
          );
        })}
      </IosList>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination size="small" count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
        </Box>
      ) : null}
    </IosPage>
  );
}
